import React, { useEffect, useRef } from 'react';
import ReactTable, { Column } from 'react-table';
import { Trans } from 'react-i18next';

interface ServiceGridTableProps<T> {
    data: T[];
    columns: Column<T>[];
    noDataText: string;
    /** Row count the table would show with the date range cleared. */
    totalCount: number;
    /** Render the "Showing X of Y Results" line (a date range is active). */
    showCount: boolean;
}

/**
 * Shared table shell for the three Inspection Status page grids.
 *
 * pageSize is controlled off the current row count (react-table v6 only reads
 * defaultPageSize once, which left blank filler rows behind after filtering)
 * and minRows is 0 so the table never pads itself out.
 */
const ServiceGridTable = <T extends {}>({
    data,
    columns,
    noDataText,
    totalCount,
    showCount
}: ServiceGridTableProps<T>) => {
    const cardRef = useRef<HTMLDivElement>(null);

    // The body keeps its scroll offset when the rows underneath it change, so
    // a filter that shrinks the table leaves it scrolled past the first result
    useEffect(() => {
        const body = cardRef.current?.querySelector('.rt-tbody');
        if (body) {
            body.scrollTop = 0;
        }
    }, [data]);

    return (
        <div className="service-grid-table-card" ref={cardRef}>
            {showCount && (
                <div className="service-grid-results-count">
                    {/*
                     * Tags in the value keep this one sentence translatable as
                     * a unit - translators must preserve <b> and both
                     * placeholders. defaults covers the window between adding
                     * the key here and publishing it in locize.
                     */}
                    <Trans
                        i18nKey="dashboardAnalytics:serviceGrid.showingResults"
                        defaults="Showing <b>{{shown}}</b> of <b>{{total}}</b> Results"
                        values={{ shown: data.length, total: totalCount }}
                        components={{ b: <strong /> }}
                    />
                </div>
            )}
            <ReactTable
                data={data}
                columns={columns}
                pageSize={data.length}
                minRows={0}
                showPagination={false}
                sortable={true}
                resizable={false}
                className={`service-grid-table -highlight${
                    data.length === 0 ? ' is-empty' : ''
                }`}
                noDataText={noDataText}
                style={{ maxHeight: '280px', overflow: 'auto' }}
            />
        </div>
    );
};

export default ServiceGridTable;
